import type { ReactNode } from 'react';
import type { Consulta } from '../../dados/contexto.js';
import { Simbolo } from '../Marca.js';

/** Bloco de carregamento no formato do conteudo, em vez de "Carregando...". */
export function Esqueleto({ className = '' }: { className?: string }) {
  return <div aria-hidden className={`esqueleto ${className}`} />;
}

export function EsqueletoPagina() {
  return (
    <div role="status" aria-label="Carregando" className="space-y-6">
      <div className="space-y-3 border-b border-borda pb-5">
        <Esqueleto className="h-3 w-40" />
        <Esqueleto className="h-9 w-72" />
        <Esqueleto className="h-4 w-56" />
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} className="h-20" />
        ))}
      </div>
      <Esqueleto className="h-80" />
    </div>
  );
}

export function EstadoVazio({ titulo, children, acao }: { titulo: string; children?: ReactNode; acao?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-(--radius-painel) border border-dashed border-borda-forte px-6 py-12 text-center">
      <p className="font-serif text-lg font-semibold text-marca">{titulo}</p>
      {children && <p className="max-w-md text-sm text-apagado">{children}</p>}
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  );
}

/** Estados de carregando e erro iguais em todas as paginas. */
export function Aguardar<T>({
  consulta,
  children,
  carregando,
}: {
  consulta: Consulta<T>;
  children: (dado: T) => ReactNode;
  carregando?: ReactNode;
}) {
  if (consulta.estado === 'carregando') return <>{carregando ?? <EsqueletoPagina />}</>;
  if (consulta.estado === 'erro')
    return (
      <p role="alert" className="rounded-(--radius-painel) border border-erro/40 bg-erro/8 px-4 py-3 text-sm text-erro">
        Não foi possível carregar: {consulta.erro.message}
      </p>
    );
  return <>{children(consulta.dado)}</>;
}

/** Lugar da foto. Sem arquivo enviado, mostra o simbolo — nunca uma foto inventada. */
export function Foto({
  className = '',
  legenda,
  src,
  alt = '',
}: {
  className?: string;
  legenda?: string;
  /** A foto de verdade, quando houver. */
  src?: string | null;
  alt?: string;
}) {
  if (src) return <img src={src} alt={alt} loading="lazy" className={`bg-superficie-2 object-cover ${className}`} />;
  return (
    <div className={`flex flex-col items-center justify-center gap-1.5 bg-superficie-2 text-apagado ${className}`}>
      <Simbolo tom="mono" className="h-auto w-1/3 max-w-24 opacity-40" />
      {legenda && <span className="px-2 text-center text-xs">{legenda}</span>}
    </div>
  );
}
